import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import feather from 'feather-icons';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import {
    getCompanyDashboardData,
    getRecentActivity
} from '../../api/web';
import { getCurrentUser } from '../../api/web';
import { SET_AUTH_USER } from '../../stores/auth';

const Dashboard = () => {
    const [dashboardData, setDashboardData] = useState({
        schemes: { count: 0 },
        schemeApplications: { total: 0, pending: 0, approved: 0, rejected: 0, underReview: 0 },
        agents: { count: 0, avgFarmersPerAgent: 0 },
        users: { count: 0 },
        inputDeliveries: { total: 0, pending: 0, confirmed: 0, delivered: 0 },
        contracts: {
            preseason: { total: 0 },
            spot: { total: 0, pending: 0, accepted: 0, rejected: 0, expired: 0 }
        }
    });
    const [recentActivities, setRecentActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pacraId, setPacraId] = useState(null);

    const token = localStorage.getItem('jwtToken');
    const dispatch = useDispatch();
    const authState = useSelector((state) => state.auth.user);
    const user = authState?.user;

    useEffect(() => {
        if (user?.company?.pacra_id) {
            setPacraId(user.company.pacra_id);
            return;
        }
        if (!token) {
            setLoading(false);
            return;
        }
        getCurrentUser()
            .then((data) => {
                if (data?.company?.pacra_id) {
                    setPacraId(data.company.pacra_id);
                    dispatch(SET_AUTH_USER({ user: data, token: authState?.token || token, isAuthenticated: true }));
                } else {
                    setLoading(false);
                }
            })
            .catch(() => setLoading(false));
    }, [token, user?.company?.pacra_id, authState?.token, dispatch]);

    const loadDashboardData = useCallback(async () => {
        if (!pacraId) {
            setLoading(false);
            return;
        }
        try {
            const [companyData, activityResponse] = await Promise.all([
                getCompanyDashboardData(pacraId),
                getRecentActivity()
            ]);

            setDashboardData({
                schemes: companyData.schemes,
                schemeApplications: companyData.schemeApplications,
                agents: companyData.agents,
                users: companyData.users,
                inputDeliveries: companyData.inputDeliveries,
                contracts: companyData.contracts
            });

            const eventToIcon = {
                'user.created': { icon: 'user-plus', iconColor: 'bg-success-light tx-success' },
                'user.updated': { icon: 'user-plus', iconColor: 'bg-success-light tx-success' },
                'scheme.created': { icon: 'book-open', iconColor: 'bg-warning-light tx-orange' },
                'application.created': { icon: 'file-text', iconColor: 'bg-primary-light tx-primary' },
                'application.status_changed': { icon: 'check-circle', iconColor: 'bg-info-light tx-info' },
                'ussd.registration': { icon: 'phone', iconColor: 'bg-success-light tx-success' },
                'contract.created': { icon: 'file-text', iconColor: 'bg-primary-light tx-primary' }
            };
            const defaultIcon = { icon: 'activity', iconColor: 'bg-secondary-light tx-secondary' };
            const activityList = activityResponse?.payload && Array.isArray(activityResponse.payload) ? activityResponse.payload : [];
            const mappedActivities = activityList.slice(0, 10).map(item => {
                const { icon, iconColor } = eventToIcon[item.event] || defaultIcon;
                return {
                    id: item.id,
                    icon,
                    iconColor,
                    title: item.message || '—',
                    time: item.created_at ? moment(item.created_at).fromNow() : '—'
                };
            });
            setRecentActivities(mappedActivities);
        } catch (error) {
            console.error('Error loading dashboard data:', error);
        } finally {
            setLoading(false);
        }
    }, [pacraId]);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    useEffect(() => {
        if (!loading) feather.replace();
    }, [loading]);

    const formatNumber = (num) => (num >= 1000 ? num.toLocaleString() : String(num));
    const calculatePercentage = (part, total) => (total === 0 ? 0 : Math.round((part / total) * 100));

    if (loading) {
        return (
            <div className="content-body">
                <div className="container pd-x-0">
                    <div className="text-center pd-y-50">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="content-body">
            <div className="container pd-x-0 pd-lg-x-10 pd-xl-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><span>Menu</span></li>
                                <li className="breadcrumb-item active" aria-current="page">Dashboard</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-0 tx-spacing--1">Dashboard</h4>
                    </div>
                </div>

                <div className="row g-3">
                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-success tx-semibold mg-b-0">Total Schemes</h6>
                                <i data-feather="book-open" className="tx-success"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.schemes.count)}</h3>
                            </div>
                            <div className="mg-t-15 mt-auto">
                                <Link to="/scheme" className="d-flex align-items-center link-03 tx-12">
                                    View schemes &rarr;
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-orange tx-semibold mg-b-0">Scheme Applications</h6>
                                <i data-feather="file" className="tx-orange"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.schemeApplications.total)}</h3>
                            </div>
                            <div className="mg-t-15 mt-auto">
                                <div className="d-flex justify-content-between tx-12 mg-b-5">
                                    <span className="tx-color-03">Pending: {formatNumber(dashboardData.schemeApplications.pending)}</span>
                                    <span className="tx-color-03">Rejected: {formatNumber(dashboardData.schemeApplications.rejected)}</span>
                                </div>
                                <div className="progress flex-fill ht-4">
                                    <div className="progress-bar bg-warning" role="progressbar" style={{ width: `${calculatePercentage(dashboardData.schemeApplications.pending, dashboardData.schemeApplications.total)}%` }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-success tx-semibold mg-b-0">Total Agents</h6>
                                <i data-feather="user-plus" className="tx-success"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.agents.count)}</h3>
                            </div>
                            <div className="mg-t-15 mt-auto">
                                <p className="tx-12 tx-color-03 mg-b-0">Avg Farmers/Agent: {formatNumber(dashboardData.agents.avgFarmersPerAgent)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-purple tx-semibold mg-b-0">Input Deliveries</h6>
                                <i data-feather="truck" className="tx-purple"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.inputDeliveries.total)}</h3>
                            </div>
                            <div className="mg-t-15 mt-auto">
                                <div className="d-flex justify-content-between tx-12 mg-b-5">
                                    <span className="tx-color-03">Pending: {formatNumber(dashboardData.inputDeliveries.pending)}</span>
                                    <span className="tx-color-03">Delivered: {formatNumber(dashboardData.inputDeliveries.delivered)}</span>
                                </div>
                                <div className="progress ht-4">
                                    <div className="progress-bar bg-warning" role="progressbar" style={{ width: `${calculatePercentage(dashboardData.inputDeliveries.pending, dashboardData.inputDeliveries.total)}%` }}></div>
                                    <div className="progress-bar bg-info" role="progressbar" style={{ width: `${calculatePercentage(dashboardData.inputDeliveries.confirmed, dashboardData.inputDeliveries.total)}%` }}></div>
                                    <div className="progress-bar bg-success" role="progressbar" style={{ width: `${calculatePercentage(dashboardData.inputDeliveries.delivered, dashboardData.inputDeliveries.total)}%` }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-primary tx-semibold mg-b-0">Total Users</h6>
                                <i data-feather="users" className="tx-primary"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.users.count)}</h3>
                            </div>
                            <div className="mg-t-15 mt-auto">
                                <Link to="/user" className="link-03 tx-12">View users &rarr;</Link>
                            </div>
                        </div>
                    </div>

                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-info tx-semibold mg-b-0">Preseason Contracts</h6>
                                <i data-feather="clipboard" className="tx-info"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.contracts.preseason.total)}</h3>
                            </div>
                            <div className="mg-t-15 mt-auto">
                                <Link to="/preseason_contract" className="link-03 tx-12">View contracts &rarr;</Link>
                            </div>
                        </div>
                    </div>

                    <div className="col-sm-6 col-lg-3">
                        <div className="card card-body h-100 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mg-b-10">
                                <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-teal tx-semibold mg-b-0">Spot Contracts</h6>
                                <i data-feather="file-text" className="tx-teal"></i>
                            </div>
                            <div className="d-flex d-lg-block d-xl-flex align-items-end mg-b-10">
                                <h3 className="tx-normal tx-rubik mg-b-0 mg-r-5 lh-1">{formatNumber(dashboardData.contracts.spot.total)}</h3>
                            </div>
                            <div className="mg-t-15 mt-auto">
                                <div className="d-flex justify-content-between tx-12 mg-b-5">
                                    <span className="tx-color-03">Pending: {formatNumber(dashboardData.contracts.spot.pending)}</span>
                                    <span className="tx-color-03">Accepted: {formatNumber(dashboardData.contracts.spot.accepted)}</span>
                                </div>
                                <Link to="/sell_request" className="link-03 tx-12">View sell requests &rarr;</Link>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row g-2 mg-t-10">
                    <div className="col-lg-6">
                        <div className="card">
                            <div className="card-header pd-t-20 pd-b-15 bd-b-0">
                                <h6 className="mg-b-0">Scheme Application Pipeline</h6>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-primary-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-primary">{formatNumber(dashboardData.schemeApplications.total)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-primary">Applied</h6>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-warning-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-warning">{formatNumber(dashboardData.schemeApplications.underReview)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-warning">Under Review</h6>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-success-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-success">{formatNumber(dashboardData.schemeApplications.approved)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-success">Approved</h6>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-danger-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-danger">{formatNumber(dashboardData.schemeApplications.rejected)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-danger">Rejected</h6>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-center mg-t-20">
                                    <Link to="/scheme_application" className="btn btn-sm btn-primary btn-uppercase">View applications</Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-6">
                        <div className="card">
                            <div className="card-header pd-t-20 pd-b-15 bd-b-0">
                                <h6 className="mg-b-0">Input Delivery Pipeline</h6>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-warning-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-warning">{formatNumber(dashboardData.inputDeliveries.pending)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-warning">Pending</h6>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-info-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-info">{formatNumber(dashboardData.inputDeliveries.confirmed)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-info">Confirmed</h6>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-success-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-success">{formatNumber(dashboardData.inputDeliveries.delivered)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-success">Delivered</h6>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <div className="text-center">
                                            <div className="wd-60 ht-60 bg-primary-light rounded-circle d-flex align-items-center justify-content-center mx-auto mg-b-10">
                                                <h4 className="tx-normal tx-rubik mg-b-0 tx-primary">{formatNumber(dashboardData.inputDeliveries.total)}</h4>
                                            </div>
                                            <h6 className="tx-uppercase tx-11 tx-spacing-1 tx-semibold mg-b-5 tx-primary">Total</h6>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-center mg-t-20">
                                    <Link to="/input_delivery" className="btn btn-sm btn-primary btn-uppercase">View deliveries</Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row g-2 mg-t-10">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-header pd-b-0 pd-x-20 bd-b-0">
                                <div className="d-sm-flex align-items-center justify-content-between">
                                    <h6 className="mg-b-0">Recent Activity</h6>
                                    <Link to="/activity" className="link-03 tx-12">View All &rarr;</Link>
                                </div>
                            </div>
                            <div className="card-body pd-20">
                                <ul className="activity tx-13">
                                    {recentActivities.length === 0 ? (
                                        <li className="activity-item">
                                            <div className="activity-body">
                                                <p className="mg-b-0 tx-color-03">No recent activity</p>
                                            </div>
                                        </li>
                                    ) : (
                                        recentActivities.map((activity) => (
                                            <li key={activity.id} className="activity-item">
                                                <div className={`activity-icon ${activity.iconColor}`}>
                                                    <i data-feather={activity.icon}></i>
                                                </div>
                                                <div className="activity-body">
                                                    <p className="mg-b-2">{activity.title}</p>
                                                    <small className="tx-color-03">{activity.time}</small>
                                                </div>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
